import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/i18n";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Hero = () => {
  const { t } = useLanguage();
  const stats = [
    { value: "20+", label: t.hero.stat1 },
    { value: "60+", label: t.hero.stat2 },
    { value: "12", label: t.hero.stat3 },
  ];

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-[#0B1F33]">
      {/* Background image with slow drift */}
      <motion.div
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 12, ease: "easeOut" }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.hero}
          alt="Architettura moderna in vetro e acciaio"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F33] via-[#0B1F33]/80 to-[#0B1F33]/45" />
      <div className="absolute inset-0 blueprint-grid-dark opacity-40" />

      <div className="relative z-10 w-full max-w-[1320px] mx-auto px-6 lg:px-10 pb-16 pt-32">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-4xl">
          <motion.div variants={item} className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#B87333]" />
            <span className="overline text-[#B87333]">{t.hero.overline}</span>
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display text-white font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-6 text-white/90 text-lg lg:text-xl max-w-2xl font-light leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p variants={item} className="mt-4 text-white/60 text-sm lg:text-base max-w-xl leading-relaxed">
            {t.hero.text}
          </motion.p>

          <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center justify-center gap-2 bg-[#B87333] text-white text-sm font-semibold px-7 py-4 hover:bg-white hover:text-[#0B1F33] transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("services")}
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white text-sm font-semibold px-7 py-4 hover:bg-white hover:text-[#0B1F33] transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div variants={item} className="mt-16 grid grid-cols-3 gap-6 max-w-xl border-t border-white/15 pt-8">
            {stats.map((s) => (
              <div key={s.label} data-testid="hero-stat">
                <div className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight">{s.value}</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-white/50 leading-tight">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-10 hidden lg:flex items-center gap-2 text-white/50 text-xs tracking-widest uppercase"
      >
        <span>Scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
